import { useEffect } from "react";
import { motion } from "framer-motion";
import logoWhite from "@/assets/logo-white.svg";
import { usePageSEO } from "@/hooks/use-page-seo";
import Footer from "@/components/Footer";
import LastYearSection from "@/components/LastYearSection";

const HERO_IMAGE_URL =
  "https://firebasestorage.googleapis.com/v0/b/yrd-2026.firebasestorage.app/o/Yacht%20Rock%20Detroit_Crowd%20Shot%20Sunset%20View%20of%20Stage%20and%20Audience.webp?alt=media";

const GLEAM_URL = "https://gleam.io/5Y7Hs/the-all-aboard-giveaway";

const perks = [
  "Open bar — all weekend",
  "Free food — all weekend",
  "25+ live acts on the water",
  "Admirals Lounge + priority entry",
];

const TicketGiveaway = () => {
  usePageSEO({
    title: "Win 4 VIP Passes — Yacht Rock Detroit Giveaway",
    description:
      "Enter to win 4 VIP passes to Yacht Rock Detroit 2026 — you plus the 3 friends you tag. Open bar, free food, and 25+ live acts on the Detroit River. July 17–18 at The Roostertail.",
    canonical: "https://yachtrockdetroit.com/ticket-giveaway",
  });

  // Load the Gleam widget script once on mount.
  useEffect(() => {
    const SCRIPT_SRC = "https://widget.gleamjs.io/e.js";
    let script = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE_URL})` }}
        />
        <div className="absolute inset-0 bg-black/55" />

        {/* Logo */}
        <div className="absolute top-0 left-0 right-0 z-20 px-6 pt-8 md:pt-10 flex justify-center">
          <a href="/" className="block">
            <img src={logoWhite} alt="Yacht Rock Detroit" className="h-20 md:h-28 w-auto" />
          </a>
        </div>

        <div className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-44 md:pt-44 pb-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.p
              className="text-yacht-yellow text-xs md:text-sm font-bold tracking-[0.3em] uppercase mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              The All Aboard Giveaway
            </motion.p>

            <motion.h1
              className="text-white font-bold leading-[0.95] mb-7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span
                className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
                style={{ fontFamily: "Georgia, Times New Roman, serif", fontWeight: 300 }}
              >
                Win 4 VIP Passes
              </span>
              <span className="block text-2xl sm:text-3xl md:text-4xl italic mt-4 text-white/90">
                You + the 3 friends you tag.
              </span>
            </motion.h1>

            <motion.p
              className="text-yacht-yellow/95 text-base md:text-lg font-semibold tracking-wide mb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              July 17–18, 2026 · The Roostertail · Detroit
            </motion.p>

            <motion.a
              href="#enter"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="btn-primary inline-block px-10 md:px-14 py-5 md:py-6 text-lg md:text-2xl font-bold tracking-wide uppercase"
            >
              Enter to Win
            </motion.a>
          </div>
        </div>
      </section>

      {/* PRIZE + ENTRY */}
      <section id="enter" className="relative py-20 md:py-28 px-6 bg-yacht-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="text-center mb-12 md:mb-14"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-yacht-blue text-xs md:text-sm font-bold tracking-[0.3em] uppercase mb-4">
              ★ The Prize ★
            </p>
            <h2 className="text-yacht-charcoal text-4xl md:text-6xl font-bold mb-5 leading-[1.05]">
              The best seat on the water.
            </h2>
            <p className="text-yacht-charcoal/70 text-base md:text-lg max-w-xl mx-auto">
              Four full-weekend VIP passes — the smoothest way to do Yacht Rock Detroit.
            </p>
          </motion.div>

          <motion.ul
            className="grid sm:grid-cols-2 gap-4 mb-14 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {perks.map((perk) => (
              <li
                key={perk}
                className="flex items-center gap-3 rounded-2xl bg-white/70 border border-yacht-charcoal/10 px-5 py-4"
              >
                <span className="text-yacht-blue text-lg font-bold">✓</span>
                <span className="text-yacht-charcoal font-semibold text-sm md:text-base">{perk}</span>
              </li>
            ))}
          </motion.ul>

          {/* Gleam widget embed */}
          <motion.div
            className="bg-white rounded-3xl border border-yacht-charcoal/10 shadow-lg p-4 md:p-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <a className="e-widget no-button" href={GLEAM_URL} rel="nofollow">
              The All Aboard Giveaway
            </a>
          </motion.div>

          <p className="mt-8 text-center text-yacht-charcoal/50 text-xs md:text-sm max-w-xl mx-auto">
            21+ to enter (open bar). No purchase necessary. Not affiliated with or endorsed by
            Instagram. Full rules on the entry form.
          </p>
        </div>
      </section>

      {/* REELS — last year's energy */}
      <LastYearSection />

      <Footer />
    </div>
  );
};

export default TicketGiveaway;
